// src/components/admin/forms/MachineForm.js
import React, { useState, useContext, useEffect } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { DashboardContext } from "../../../contexts/AdminContext";
import FormNotification, {
  useFormNotification,
} from "../../common/FormNotification";

const MachineForm = ({ show, handleClose, initialData, onSuccess }) => {
  const { sectors, addMachine, updateMachine } = useContext(DashboardContext);

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    sector: "",
    status: "Operational",
    activation_date: "",
    description: "",
  });

  const { notif, notifySuccess, notifyError, clearNotif } =
    useFormNotification();
  const [loading, setLoading] = useState(false);

  // Debug
  useEffect(() => {
    console.log("sectors from context full: ", sectors);
  }, [sectors]);

  // Reset / isi form ketika modal dibuka atau data awal berubah
  useEffect(() => {
    if (!show) {
      setFormData({
        name: "",
        code: "",
        sector: "",
        status: "Operational",
        activation_date: "",
        description: "",
      });
      clearNotif();
      setLoading(false);
      return;
    }

    if (initialData) {
      setFormData({
        name: initialData.name || "",
        code: initialData.code || "",
        sector: initialData.sector?.id || initialData.sector_id || "",
        status: initialData.status || "Operational",
        activation_date: initialData.activation_date
          ? initialData.activation_date.split("T")[0]
          : "",
        description: initialData.description || "",
      });
    } else {
      setFormData({
        name: "",
        code: "",
        sector: "",
        status: "Operational",
        activation_date: "",
        description: "",
      });
    }
  }, [show, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    clearNotif();

    const sectorId = Number(formData.sector); // sekarang sudah number
    const sectorObj = sectors.find((s) => s.id == sectorId);

    // Validasi sector
    if (!sectorId || !sectorObj) {
      notifyError("Sector tidak valid, silakan pilih sector yang ada");
      setLoading(false);
      return;
    }

    // Validasi nama dan kode mesin
    if (!formData.name.trim() || !formData.code.trim()) {
      notifyError("Nama dan kode mesin wajib diisi");
      setLoading(false);
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        code: formData.code.trim(),
        status: formData.status,
        sector_id: Number(formData.sector),
        activation_date: formData.activation_date || null,
        description: formData.description || null,
      };

      let result;
      if (initialData) {
        result = await updateMachine(initialData.id, payload);
      } else {
        result = await addMachine(payload);
      }

      if (result.success) {
        notifySuccess(
          initialData
            ? "Data mesin berhasil diperbarui."
            : "Mesin baru berhasil ditambahkan."
        );
        // Biarkan loading tetap aktif (tombol disabled) hingga modal ditutup,
        // supaya banner sukses sempat terlihat & mencegah submit ganda.
        setTimeout(() => {
          if (onSuccess) {
            onSuccess(); // refresh list + tutup modal
          } else {
            handleClose();
          }
        }, 1300);
      } else {
        notifyError(result.error || "Terjadi kesalahan saat menyimpan data");
        setLoading(false);
      }
    } catch (err) {
      notifyError(err.message || "Terjadi kesalahan saat menyimpan data");
      console.error("Error submit MachineForm:", err);
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          {initialData ? "Edit Mesin" : "Tambah Mesin Baru"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormNotification notif={notif} onClose={clearNotif} />

        {sectors.length === 0 && (
          <Alert variant="warning">Memuat sektor... Silakan tunggu</Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Kode Mesin</Form.Label>
            <Form.Control
              type="text"
              placeholder="Masukkan kode mesin"
              name="code"
              value={formData.code}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Nama Mesin</Form.Label>
            <Form.Control
              type="text"
              placeholder="Masukkan nama mesin"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Sektor</Form.Label>
            <Form.Select
              name="sector"
              value={formData.sector}
              onChange={handleChange}
              required
              disabled={sectors.length === 0}
            >
              <option value="">Pilih Sektor</option>
              {sectors.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || "-"}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="Operational">Beroperasi</option>
              <option value="Need Maintenance">Butuh Perbaikan</option>
              <option value="Under Maintenance">Sedang Diperbaiki</option>
              <option value="Down">Tidak Aktif</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Tanggal Mulai Aktif</Form.Label>
            <Form.Control
              type="date"
              name="activation_date"
              value={formData.activation_date}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Keterangan Mesin</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              placeholder="Masukkan keterangan mesin"
              value={formData.description}
              onChange={handleChange}
            />
          </Form.Group>

          <div className="d-flex justify-content-end mt-4">
            <Button variant="secondary" onClick={handleClose} className="me-2">
              Batal
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={loading || sectors.length === 0}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" /> Menyimpan...
                </>
              ) : (
                "Simpan"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default MachineForm;
